import { useRef } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useEntityFinderContext } from "../context/EntityFinderContext";
import { Task } from "@/bryntum-lib/models/Task";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { ENTITY_TYPES } from "@/other/constants/OtherKeys";
import { TaskRenderer } from "@/modern/components/task-renderer";
import { getFontSize } from "@/react/context/bryntumContext";

export const TaskTab = () => {
  const { data, handleClose } = useEntityFinderContext();
  const { entityManager } = useSwivuBryntumContext();
  const tasks = data as Task[];
  const containerRef = useRef<HTMLDivElement>(null);

  const gap = 5;
  const rowVirtualizer = useVirtualizer({
    count: tasks.length,
    getScrollElement: () => containerRef.current,
    estimateSize: () => getFontSize() * 2.5 + gap,
    overscan: 8
  });

  const virtualItems = rowVirtualizer.getVirtualItems();
  const totalSize = rowVirtualizer.getTotalSize();

  const handleTaskClick = (task: Task) => {
    entityManager.openEntityManager({ entityType: ENTITY_TYPES.TASK, entityId: task.id });
    handleClose();
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div ref={containerRef} className="flex-1 overflow-auto bg-background user-select-none">
        {tasks.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-muted-foreground p-8 text-center">
            <svg className="w-12 h-12 mb-4 opacity-40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
              />
            </svg>
            <p className="text-sm">No tasks found</p>
          </div>
        ) : (
          <div className="relative w-full" style={{ height: `${totalSize}px` }}>
            {virtualItems.map((virtualRow) => {
              const task = tasks[virtualRow.index];
              return (
                <div
                  key={task.id}
                  className="swivu-entity-finder-drag-handler absolute left-0 top-0 w-full"
                  data-swivu-task-id={task.id}
                  style={{
                    height: `${virtualRow.size}px`,
                    transform: `translateY(${virtualRow.start}px)`
                  }}
                >
                  <div className="rounded-md truncate" style={{ height: `${virtualRow.size - gap}px`, marginBottom: `${gap}px` }}>
                    <TaskRenderer record={task} onClick={handleTaskClick} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
